import React, { useState, useEffect } from 'react';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import './AdvancedEcommerceOrder.css';
import { local_signage, local_url } from '../../Const';
import Header from '../../Header';
import * as XLSX from 'xlsx';

export const PypReport = () => {
  // State variables
  const [sfAreas, setSfAreas] = useState([]);
  const [selectedSfAreaId, setSelectedSfAreaId] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'day'));
  const [toDate, setToDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [fetchingData, setFetchingData] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(100);
  const [tableWidth, setTableWidth] = useState(0);
  const [tableHeight, setTableHeight] = useState(0);
  const [hasDataChanged, setHasDataChanged] = useState(false);

  // User authentication
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // Order type options
  const orderTypes = [
    { value: 'PYP', label: 'PYP Orders' },
    { value: 'SBS', label: 'Subscription Orders' }
  ];

  // DatePicker styles
  const datePickerStyle = {
    width: '100%',
    maxWidth: '150px',
  };

  // Calculate table dimensions based on viewport
  useEffect(() => {
    const updateTableDimensions = () => {
      const windowHeight = window.innerHeight;
      const estimatedOtherComponentsHeight = 300;
      const calculatedHeight = windowHeight - estimatedOtherComponentsHeight;

      setTableHeight(Math.max(calculatedHeight, 400));
      setTableWidth(window.innerWidth - 40);
    };

    updateTableDimensions();
    window.addEventListener('resize', updateTableDimensions);

    return () => {
      window.removeEventListener('resize', updateTableDimensions);
    };
  }, []);

  // Check for authentication on component mount
  useEffect(() => {
    if (!authToken) {
      setError("Authorization token is missing. Please log in again.");
      redirectToLogin();
      return;
    }

    fetchSfAreas();
  }, []);

  // Redirect to login
  const redirectToLogin = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  // Filter data based on search query and order type
  useEffect(() => {
    if (data.length > 0) {
      let filtered = [...data];

      // Filter by order type if selected
      if (selectedOrderType) {
        filtered = filtered.filter(item => {
          if (selectedOrderType === 'PYP') {
            return item.SaleOrderID 
          } else if (selectedOrderType === 'SUB') {
            return item.SaleOrderID && item.SaleOrderID.includes('SUB-');
          }
          return true;
        });
      }

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(item =>
          (item.SaleOrderID && item.SaleOrderID.toLowerCase().includes(query)) ||
          (item.CustID && item.CustID.toString().toLowerCase().includes(query))
        );
      }

      // Apply sorting if configured
      if (sortConfig.key && sortConfig.direction) {
        filtered.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        });
      }

      setFilteredData(filtered);
      setCurrentPage(1);
    } else {
      setFilteredData([]);
    }
  }, [data, searchQuery, selectedOrderType, sortConfig]);

  // Fetch SF Areas
  const fetchSfAreas = async () => {
    if (!userId || !authToken) {
      setError("User credentials not found. Please log in again.");
      redirectToLogin();
      return;
    }

    try {
      const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setError("Session expired. Please log in again.");
          redirectToLogin();
          return;
        }
        throw new Error("Failed to fetch SF Areas");
      }

      const data = await response.json();
      setSfAreas(data);
    } catch (error) {
      setError("Failed to fetch SF Areas. Please try again later.");
    }
  };

  // Handle SF Area change
  const handleSfAreaChange = (e) => {
    setSelectedSfAreaId(e.target.value);
    setHasDataChanged(true);
  };

  // Handle Order Type change
  const handleOrderTypeChange = (e) => {
    setSelectedOrderType(e.target.value);
    setHasDataChanged(true);
  };

  // Handle date changes
  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
    setHasDataChanged(true);
  };

  const handleToDateChange = (newValue) => {
    setToDate(newValue);
    setHasDataChanged(true);
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Format date for API request
  const formatDateForApi = (date) => {
    return date.format('YYYYMMDD');
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedSfAreaId) {
      setError("Please select an Area");
      return;
    }

    if (!authToken) {
      setError("Authorization token is missing. Please log in again.");
      redirectToLogin();
      return;
    }

    setFetchingData(true);
    setError('');

    try {
      const formattedFromDate = formatDateForApi(fromDate);
      const formattedToDate = formatDateForApi(toDate);

      const url = `${local_signage}/api/v1/advanceecommerceorder?AreaId=${selectedSfAreaId}&AdvanceOrderType=${selectedOrderType}&FromDate=${formattedFromDate}&ToDate=${formattedToDate}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setError("Session expired. Please log in again.");
          redirectToLogin();
          return;
        }
        throw new Error("Failed to fetch data");
      }

      const responseData = await response.json();
      setData(responseData);
      setHasDataChanged(false);
      setFetchingData(false);
    } catch (error) {
      setError("Failed to fetch data. Please try again later.");
      setFetchingData(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Format date and time
  const formatDateTime = (dateTimeString, type) => {
    if (!dateTimeString) return "-";

    const date = new Date(dateTimeString);

    if (isNaN(date.getTime())) return "-";

    if (type === "date") {
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } else if (type === "time") {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }

    return "-";
  };

  // Export data to CSV
// Export data to XLSX
const exportToXLSX = () => {
  if (filteredData.length === 0) return;

  setExportLoading(true);

  try {
    // Create headers
    const headers = [
      "Sr.No", "Sale Order ID", "Order Date", "Order Time", "Customer Name",
      "Customer ID", "Mobile", "Total Amount", "Order Type", 
      "Receiving Branch", "Channel ID"
    ];

    // Format data for XLSX with proper date format
    const xlsxData = filteredData.map((item, index) => ({
      "Sr.No": index + 1,
      "Sale Order ID": item.SaleOrderID || "-",
      "Order Date": formatDateTime(item.CreatedDate, "date"), // Already in dd-mm-yyyy format
      "Order Time": formatDateTime(item.CreatedDate, "time"),
      "Customer Name": item.CustomerName || "-",
      "Customer ID": item.CustID || "-",
      "Mobile": item.Mobile || "-",
      "Total Amount": item.TotalAmount?.toFixed(2) || "0.00",
      "Order Type": item.OrderType === 'PREPD' ? 'PYP' : (item.OrderType || "-"),
      "Receiving BranchID": item.ReceivingBranchID || "-",
      "Receiving Branch": item.ReceivingBarnch || "-",
      "Channel ID": item.ChannelId || "-"
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(xlsxData);

    // Set column widths
    const colWidths = [
      { wch: 8 },  // Sr.No
      { wch: 20 }, // Sale Order ID
      { wch: 12 }, // Order Date
      { wch: 10 }, // Order Time
      { wch: 25 }, // Customer Name
      { wch: 12 }, // Customer ID
      { wch: 12 }, // Mobile
      { wch: 12 }, // Total Amount
      { wch: 12 }, // Order Type
      { wch: 20 }, // Receiving BranchID
      { wch: 25 }, // Receiving Branch
      { wch: 12 }  // Channel ID
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Advance Orders");

    // Generate filename with current date
    const filename = `AdvanceOrders_${new Date().toISOString().slice(0, 10)}.xlsx`;

    // Write and download file
    XLSX.writeFile(wb, filename);

    setExportLoading(false);
  } catch (error) {
    setError("Failed to export data. Please try again later.");
    setExportLoading(false);
  }
};

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const displayData = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    let pageNumbers = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (startPage > 1) {
        pageNumbers = [1, '...', ...pageNumbers.slice(2)];
      }

      if (endPage < totalPages) {
        pageNumbers = [...pageNumbers.slice(0, -2), '...', totalPages];
      }
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Sort Header Component
  const SortHeader = ({ column, label }) => (
    <th onClick={() => handleSort(column)} className="advance-sortable-header">
      {label}
      {sortConfig.key === column && (
        <span className="advance-sort-indicator">{sortConfig.direction === 'ascending' ? '▲' : '▼'}</span>
      )}
    </th>
  );

  return (
    <>
      <Header />

      <div className="advance-ecommerce-order-container">
        {error && <div className="advance-error-message">{error}</div>}

        <div className="advance-filters-container">
          <div className="advance-filter-row">
            <div className="advance-filter-group">
              <select
                className="advance-filter-dropdown"
                value={selectedSfAreaId}
                onChange={handleSfAreaChange}
              >
                <option value="" disabled>
                  Select Area
                </option>
                <option value="ALL">
                  ALL
                </option>
                {sfAreas.map((area) => (
                  <option key={area.sfAreaId} value={area.sfAreaId}>
                    {area.sfAreaName} ({area.sfAreaId})
                  </option>
                ))}
              </select>

              <select
                className="advance-filter-dropdown"
                value={selectedOrderType}
                onChange={handleOrderTypeChange}
              >
                <option value="" disabled>
                  Select Order Type
                </option>
                {orderTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="advance-date-filters">
              <div className="advance-date-picker-wrapper">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    format="DD-MM-YYYY"
                    sx={datePickerStyle}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined"
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="advance-date-separator">
                <span>-</span>
              </div>

              <div className="advance-date-picker-wrapper">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={handleToDateChange}
                    format="DD-MM-YYYY"
                    sx={datePickerStyle}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined"
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>

            <div className="advance-action-buttons">
              <button
                onClick={handleSubmit}
                className="submit-button"
                disabled={loading || !selectedSfAreaId || !selectedOrderType}
              >
                {fetchingData ? "Loading..." : "Submit"}
              </button>
              <button
                onClick={exportToXLSX}
                className="export-button"
                disabled={loading || filteredData.length === 0 || exportLoading || hasDataChanged}
                title={hasDataChanged ? "Please submit the form to apply changes before exporting" : ""}
              >
                {exportLoading ? "Exporting..." : "Export"}
              </button>
            </div>
            
            <div className="advance-search-row">
              <div className="advance-search-container">
                <input
                  type="text"
                  placeholder="Search Sale Order ID or Customer ID"
                  value={searchQuery}
                  onChange={handleSearch}
                  className="advance-search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Loading Indicator */}
        {fetchingData && (
          <div className="advance-loader-overlay">
            <div className="advance-loader-container">
              <CircularProgress size={60} />
              <p className="advance-loader-text">Loading data...</p>
            </div>
          </div>
        )}

        {/* Pagination Controls - Top */}
        {/* {filteredData.length > recordsPerPage && (
          <div className="advance-pagination-container">
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="advance-pagination-button"
            >
              First
            </button>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="advance-pagination-button"
            >
              Prev
            </button>

            {pageNumbers.map((number, index) => (
              number === "..." ?
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span> :
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`pagination-button ${currentPage === number ? 'pagination-active' : ''}`}
                >
                  {number}
                </button>
            ))}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="advance-pagination-button"
            >
              Next
            </button>
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="advance-pagination-button"
            >
              Last
            </button>
            <span className="advance-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        )} */}

        {/* Table Component - Cleaned and Reorganized */}
        <div className="advance-table-container" style={{ height: `${tableHeight}px` }}>
          <div className="advance-table-wrapper">
            <table className="advance-data-table">
              <thead>
                <tr>
                  <th className="sticky-col">Sr.No</th>
                  <SortHeader column="SaleOrderID" label="Sale Order ID" />
                  <SortHeader column="CreatedDate" label="Order Date" />
                  <SortHeader column="CreatedDate" label="Order Time" />
                  <SortHeader column="CustomerName" label="Customer Name" />
                  <SortHeader column="CustID" label="Customer ID" />
                  <SortHeader column="Mobile" label="Mobile" />
                  <SortHeader column="TotalAmount" label="Total Amount" />
                  <SortHeader column="OrderType" label="Order Type" />
                  <SortHeader column="ReceivingBranchID" label="Receiving BranchID" />
                  <SortHeader column="ReceivingBarnch" label="Receiving Branch" />
                  <SortHeader column="ChannelId" label="Channel ID" />
                </tr>
              </thead>
              <tbody>
                {displayData.length > 0 ? (
                  displayData.map((item, index) => {
                    const actualIndex = (currentPage - 1) * recordsPerPage + index;
                    return (
                      <tr key={index}>
                        <td className="sticky-col">{actualIndex + 1}</td>
                        <td>{item.SaleOrderID || "-"}</td>
                        <td>{formatDateTime(item.CreatedDate, "date")}</td>
                        <td>{formatDateTime(item.CreatedDate, "time")}</td>
                        <td>{item.CustomerName || "-"}</td>
                        <td>{item.CustID || "-"}</td>
                        <td>{item.Mobile || "-"}</td>
                        <td>₹{item.TotalAmount?.toFixed(2) || "0.00"}</td>
                        <td>{item.OrderType === 'PREPD' ? 'PYP' : (item.OrderType || "-")}</td>
                        <td>{item.ReceivingBranchID || "-"}</td>
                        <td>{item.ReceivingBarnch || "-"}</td>
                        <td>{item.ChannelId || "-"}</td>
                      </tr>
                    );
                  })
                ) : fetchingData ? (
                  <tr>
                    <td colSpan="13" className="advance-no-data-message">
                      Loading data...
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan="13" className="advance-no-data-message">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination Controls - Bottom */}
{filteredData.length > recordsPerPage && (
  <div className="advance-pagination-container">
    <button
      onClick={() => paginate(1)}
      disabled={currentPage === 1}
      className="advance-pagination-button"
    >
      First
    </button>
    <button
      onClick={() => paginate(currentPage - 1)}
      disabled={currentPage === 1}
      className="advance-pagination-button"
    >
      Prev
    </button>

    {pageNumbers.map((number, index) => (
      number === "..." ?
        <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span> :
        <button
          key={number}
          onClick={() => paginate(number)}
          className={`pagination-button ${currentPage === number ? 'pagination-active' : ''}`}
        >
          {number}
        </button>
    ))}

    <button
      onClick={() => paginate(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="advance-pagination-button"
    >
      Next
    </button>
    <button
      onClick={() => paginate(totalPages)}
      disabled={currentPage === totalPages}
      className="advance-pagination-button"
    >
      Last
    </button>
    <span className="advance-pagination-info">
      Page {currentPage} of {totalPages}
    </span>
  </div>
)}
      </div>
    </>
  );
};

export default PypReport;